import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import dashboardCustomizationService from '../../../services/dashboardCustomizationService';

const DashboardCustomizationModal = ({ onClose, onSave }) => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState(null);

  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      setLoading(true);
      const data = await dashboardCustomizationService?.getAllWidgets();
      setWidgets(data);
    } catch (err) {
      setError(err?.message || 'Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = (widgetId) => {
    setWidgets(prev => prev?.map(w => 
      w?.id === widgetId ? { ...w, isVisible: !w?.isVisible } : w
    ));
  };

  const handleDragStart = (e, widget) => {
    setDraggedWidget(widget);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetWidget) => {
    e?.preventDefault();
    
    if (!draggedWidget || draggedWidget?.id === targetWidget?.id) return;

    const draggedIndex = widgets?.findIndex(w => w?.id === draggedWidget?.id);
    const targetIndex = widgets?.findIndex(w => w?.id === targetWidget?.id);

    const newWidgets = [...widgets];
    newWidgets?.splice(draggedIndex, 1);
    newWidgets?.splice(targetIndex, 0, draggedWidget);

    const reordered = newWidgets?.map((w, index) => ({
      ...w,
      positionOrder: index + 1
    }));

    setWidgets(reordered);
    setDraggedWidget(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Update widget visibility and order
      await Promise.all(
        widgets?.map(widget => 
          dashboardCustomizationService?.updateWidget(widget?.id, {
            isVisible: widget?.isVisible,
            positionOrder: widget?.positionOrder
          })
        )
      );

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save customization');
    } finally {
      setSaving(false);
    }
  };

  const getWidgetIcon = (type) => {
    const icons = {
      compliance_score: 'Shield',
      alert_summary: 'Bell',
      violation_trends: 'TrendingUp',
      department_performance: 'Users',
      payroll_sync_status: 'RefreshCw',
      cost_analysis: 'DollarSign',
      deadline_countdown: 'Clock',
      kpi_metric: 'BarChart'
    };
    return icons?.[type] || 'Layout';
  };

  const getSizeLabel = (size) => {
    const labels = {
      small: 'Small (1 col)',
      medium: 'Medium (2 cols)',
      large: 'Large (3 cols)',
      full: 'Full Width'
    };
    return labels?.[size] || size;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full p-6">
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader" size={32} color="var(--color-muted-foreground)" className="animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Layout" size={24} />
              Customize Dashboard
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Drag to reorder, toggle visibility for each widget
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div className="space-y-2">
            {widgets?.map((widget) => (
              <div
                key={widget?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, widget)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, widget)}
                className={`
                  bg-muted/30 rounded-lg p-4 cursor-move transition-all
                  hover:bg-muted/50 hover:shadow-md
                  ${draggedWidget?.id === widget?.id ? 'opacity-50' : ''}
                `}
              >
                <div className="flex items-center gap-4">
                  <Icon name="GripVertical" size={20} color="var(--color-muted-foreground)" />
                  
                  <div className={`
                    w-10 h-10 rounded-md flex items-center justify-center
                    ${widget?.isVisible ? 'bg-primary/10' : 'bg-muted'}
                  `}>
                    <Icon 
                      name={getWidgetIcon(widget?.widgetType)} 
                      size={20} 
                      color={widget?.isVisible ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} 
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground">{widget?.widgetName}</h3>
                      {widget?.isDefault && (
                        <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Icon name="Maximize2" size={12} />
                        {getSizeLabel(widget?.widgetSize)}
                      </span>
                      <span>•</span>
                      <span>{widget?.configurations?.length || 0} settings</span>
                    </div>
                  </div>

                  <Checkbox
                    checked={widget?.isVisible}
                    onChange={() => handleToggleVisibility(widget?.id)}
                    label="Visible"
                  />
                </div>
              </div>
            ))}
          </div>

          {widgets?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Layout" size={48} className="mx-auto mb-3 opacity-50" />
              <p className="text-sm">No widgets available</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-6 border-t border-border bg-muted/20">
          <p className="text-xs text-muted-foreground">
            {widgets?.filter(w => w?.isVisible)?.length} of {widgets?.length} widgets visible
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              iconName="Save"
              onClick={handleSave}
              loading={saving}
            >
              Save Layout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCustomizationModal;